import { Coordinate } from 'ol/coordinate';
import { VoyagePlan } from '../../../src/models/vessel-planning-model';
import { ValidationRules } from '../types/polar-chart-form-types'
import { PolarPlotResponse } from '../types/polar-schema-types';

export const validationRules: ValidationRules = {
    wave_height: {
        regex: /^\d+(\.\d+)?$/, // Allows digits with optional decimals
        min: 0,
        max: 25,
        errorMessage: 'Must be between 0 and 25 meters.'
    },
    wave_direction: {
        regex: /^\d+$/, // Integer validation
        min: 0,
        max: 359,
        errorMessage: 'Must be between 0 and 359 degrees.'
    },
    wave_period: {
        regex: /^\d+$/, // Integer validation
        min: 0,
        max: 180,
        errorMessage: 'Must be between 0 and 180 seconds.'
    },
    wind_speed: {
        regex: /^\d+(\.\d+)?$/, // Allows digits with optional decimals
        min: 0,
        max: 45,
        errorMessage: 'Must be between 0 and 45 m/s.'
    },
    swell_height: {
        regex: /^\d+(\.\d+)?$/, // Allows digits with optional decimals
        min: 0,
        max: 25,
        errorMessage: 'Must be between 0 and 25 meters.'
    },
    swell_period: {
        regex: /^\d+$/, // Integer validation
        min: 0,
        max: 180,
        errorMessage: 'Must be between 0 and 180 seconds.'
    },
    vessel_current_speed: {
        regex: /^\d+(\.\d+)?$/, // Allows digits with optional decimals
        min: 0,
        max: 28,
        errorMessage: 'Must be between 0 and 28 knots.'
    },
    vessel_max_speed: {
        regex: /^\d+(\.\d+)?$/, // Allows digits with optional decimals
        min: 0,
        max: 28,
        errorMessage: 'Must be between 0 and 28 knots.'
    },
    wind_direction: {
        regex: /^\d+$/, // Integer validation
        min: 0,
        max: 359,
        errorMessage: 'Must be between 0 and 359 degrees.'
    },
    swell_direction: {
        regex: /^\d+$/, // Integer validation
        min: 0,
        max: 359,
        errorMessage: 'Must be between 0 and 359 degrees.'
    },
    vessel_heading: {
        regex: /^\d+$/, // Integer validation
        min: 0,
        max: 360,
        errorMessage: 'Must be between 0 and 360 degrees.'
    },
    solid_gm: {
        regex: /^\d+(\.\d+)?$/,
        min: 0.6,
        max: 25,
        errorMessage: 'Solid GM must be between 0.6 m and 25.0 m.'
    },
    fluid_gm: {
        regex: /^\d+(\.\d+)?$/,
        min: 0.6,
        max: 25,
        errorMessage: 'Fluid GM must be between 0.6 m and 25.0 m.'
    },
    roll_angle_zone_limit: {
        regex: /^\d+(\.\d+)?$/,
        min: 5,
        max: 12,
        errorMessage: 'Must be between 5 and 12 degrees.'
    },
};

interface DataEntry {
    label: string;
    value: string;
    unit: string;
    id: string;
}

// Define specific groups of data
interface PolarFormData {
    waveData: DataEntry[];
    windData: DataEntry[];
    swellData: DataEntry[];
    vesselData: DataEntry[];
    gmData: DataEntry[];
    rollAngleData: DataEntry[];
    accelerationForceData: DataEntry[];
}

export const DEFAULT_TRANSVERSE_ACCELERATION_THRESHOLD = 1.22;

// Exporting the interfaces
export type { DataEntry, PolarFormData };


export const constructPayload = (polarFormData: PolarFormData, plan: VoyagePlan, coordinate: Coordinate, polarPlotData: PolarPlotResponse, imo: string) => {

    const newSpeed = Number(polarFormData.vesselData.find(data => data.id === 'vessel_current_speed')?.value) || 0;
    const newHeading = Number(polarFormData.vesselData.find(data => data.id === 'vessel_heading')?.value) || 0;
    const weather = {
        wave_height: Number(polarFormData.waveData.find(data => data.id === 'wave_height')?.value) || 0,
        swell_height: Number(polarFormData.swellData.find(data => data.id === 'swell_height')?.value) || 0,
        wave_period: Number(polarFormData.waveData.find(data => data.id === 'wave_period')?.value) || 0,
        wave_direction: Number(polarFormData.waveData.find(data => data.id === 'wave_direction')?.value) || 0,
        swell_direction: Number(polarFormData.swellData.find(data => data.id === 'swell_direction')?.value) || 0,
        wind_speed: Number(polarFormData.windData.find(data => data.id === 'wind_speed')?.value) || 0,
        wind_direction: Number(polarFormData.windData.find(data => data.id === 'wind_direction')?.value) || 0,
        current_speed: polarPlotData.weather.current_speed,
        current_direction: polarPlotData.weather.current_direction,
        swell_period: Number(polarFormData.swellData.find(data => data.id === 'swell_period')?.value) || 0,
    }
    const gmData = {
        solid_gm: Number(polarFormData.gmData.find(data => data.id === 'solid_gm')?.value) || plan.solidGm,
        fluid_gm: Number(polarFormData.gmData.find(data => data.id === 'fluid_gm')?.value) || plan.gmt
    }

    const rollAngleZoneLimit = Number(polarFormData.rollAngleData.find(data => data.id === 'roll_angle_zone_limit')?.value) || null;
    const accelerationForceLimit = Number(polarFormData.accelerationForceData?.find(data => data.id === 'acceleration_force_limit')?.value) || plan.transverseAccelerationThreshold || DEFAULT_TRANSVERSE_ACCELERATION_THRESHOLD;

    const payLoad: any = {
        transverse_metacentric_height_solid: gmData.solid_gm,
        transverse_metacentric_height: gmData.fluid_gm,
        draught_fore: plan.foreDraft,
        draught_aft: plan.aftDraft,
        latitude: coordinate[1],
        longitude: coordinate[0],
        course_over_ground: newHeading,
        speed_over_ground: newSpeed,
        forecast: null,
        weather,
        imo,
        transverse_acceleration_threshold: accelerationForceLimit,
    }

    // Only add roll_angle if roll_angle_zone_limit has a value
    if (rollAngleZoneLimit !== null && rollAngleZoneLimit !== 0) {
        payLoad.roll_angle = rollAngleZoneLimit;
    }

    return payLoad;
}